import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Plus, Edit, Trash2, Eye, EyeOff, ArrowLeft, Loader2, 
  FileText, Image, Youtube, Save, Bold, Italic, Heading2, List, ListOrdered, Minus, Type
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogManager = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const contentRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    featured_image: '',
    tags: '',
    is_published: false
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      // Use admin endpoint to get all posts (including unpublished)
      const response = await axios.get(`${API}/admin/blog`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (title) => {
    setFormData({
      ...formData,
      title,
      slug: editingPost ? formData.slug : generateSlug(title)
    });
  };

  const openEditor = (post = null) => {
    if (post) {
      setEditingPost(post);
      setFormData({
        title: post.title,
        slug: post.slug,
        excerpt: post.excerpt,
        content: post.content,
        featured_image: post.featured_image || '',
        tags: post.tags.join(', '),
        is_published: post.is_published
      });
    } else {
      setEditingPost(null);
      setFormData({
        title: '',
        slug: '',
        excerpt: '',
        content: '',
        featured_image: '',
        tags: '',
        is_published: false
      });
    }
    setEditorOpen(true);
  };

  const savePost = async () => {
    if (!formData.title || !formData.content || !formData.excerpt) {
      toast.error('Please fill in title, excerpt, and content');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t)
      };

      if (editingPost) {
        await axios.put(`${API}/blog/${editingPost.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Post updated!');
      } else {
        await axios.post(`${API}/blog`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Post created!');
      }
      
      setEditorOpen(false);
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/blog/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post deleted successfully');
      fetchPosts();
    } catch (error) {
      console.error('Delete error:', error.response?.data || error);
      toast.error(error.response?.data?.detail || 'Failed to delete post');
    }
  };

  const togglePublish = async (post) => {
    try {
      await axios.put(`${API}/blog/${post.id}`, {
        is_published: !post.is_published
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(post.is_published ? 'Post unpublished' : 'Post published!');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to update post');
    }
  };

  const insertImage = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      const alt = prompt('Enter image description (optional):', 'Image');
      const imgMarkdown = `\n![${alt || 'Image'}](${url})\n`;
      setFormData({ ...formData, content: formData.content + imgMarkdown });
    }
  };

  const insertYouTube = () => {
    const url = prompt('Enter YouTube video URL:');
    if (url) {
      const videoId = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/)?.[1];
      if (videoId) {
        const iframe = `<div class="aspect-video my-4"><iframe src="https://www.youtube.com/embed/${videoId}" frameborder="0" allowfullscreen class="w-full h-full rounded-lg"></iframe></div>`;
        setFormData({ ...formData, content: formData.content + '\n' + iframe + '\n' });
      } else {
        toast.error('Invalid YouTube URL');
      }
    }
  };

  const insertFormatting = (prefix, suffix = '') => {
    const textarea = contentRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = formData.content;
    const selected = text.substring(start, end);
    const replacement = selected
      ? `${prefix}${selected}${suffix || prefix}`
      : `${prefix}text${suffix || prefix}`;
    const newContent = text.substring(0, start) + replacement + text.substring(end);
    setFormData({ ...formData, content: newContent });
    setTimeout(() => {
      textarea.focus();
      const cursorPos = selected
        ? start + replacement.length
        : start + prefix.length;
      textarea.setSelectionRange(cursorPos, cursorPos + (selected ? 0 : 4));
    }, 0);
  };

  const insertLinePrefix = (prefix) => {
    const textarea = contentRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const text = formData.content;
    const lineStart = text.lastIndexOf('\n', start - 1) + 1;
    const newContent = text.substring(0, lineStart) + prefix + text.substring(lineStart);
    setFormData({ ...formData, content: newContent });
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + prefix.length, start + prefix.length);
    }, 0);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="blog-manager">
      <Navbar />

      <div className="pt-24 pb-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/admin')} className="-ml-4">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="font-heading text-2xl font-bold text-slate-900">Blog Manager</h1>
                <p className="text-slate-600">Create and manage your blog posts</p>
              </div>
            </div>
            <Button onClick={() => openEditor()} data-testid="new-post-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Post
            </Button>
          </div>

          {/* Posts List */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading">Your Posts</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : posts.length > 0 ? (
                <div className="divide-y divide-slate-200">
                  {posts.map((post) => (
                    <div key={post.id} className="py-4 flex items-center gap-4">
                      {post.featured_image ? (
                        <img src={post.featured_image} alt="" className="w-20 h-14 rounded-lg object-cover" />
                      ) : (
                        <div className="w-20 h-14 rounded-lg bg-slate-100 flex items-center justify-center">
                          <FileText className="w-6 h-6 text-slate-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-slate-900 truncate">{post.title}</h3>
                          <Badge variant={post.is_published ? 'default' : 'secondary'}>
                            {post.is_published ? 'Published' : 'Draft'}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span>{new Date(post.created_at).toLocaleDateString()}</span>
                          <span>{post.views} views</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={() => togglePublish(post)} data-testid={`toggle-${post.id}`}>
                          {post.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openEditor(post)} data-testid={`edit-${post.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => deletePost(post.id)} className="text-destructive" data-testid={`delete-${post.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No blog posts yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Editor Dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPost ? 'Edit Post' : 'New Post'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  placeholder="Post title"
                  data-testid="blog-title"
                />
              </div>
              <div className="space-y-2">
                <Label>Slug</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="url-friendly-slug"
                  data-testid="blog-slug"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Excerpt (Short description)</Label>
              <Textarea
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                placeholder="Brief description for previews..."
                rows={2}
                data-testid="blog-excerpt"
              />
            </div>

            <div className="space-y-2">
              <Label>Featured Image URL</Label>
              <Input
                value={formData.featured_image}
                onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                placeholder="https://..."
                data-testid="blog-image"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Content</Label>
                <span className="text-xs text-slate-400">Markdown supported</span>
              </div>
              <div className="flex items-center gap-1 p-1.5 bg-slate-100 rounded-lg border border-slate-200 flex-wrap">
                <Button type="button" variant="ghost" size="sm" className="h-8 px-2" onClick={() => insertFormatting('**')} title="Bold" data-testid="format-bold">
                  <Bold className="w-4 h-4" />
                </Button>
                <Button type="button" variant="ghost" size="sm" className="h-8 px-2" onClick={() => insertFormatting('*')} title="Italic" data-testid="format-italic">
                  <Italic className="w-4 h-4" />
                </Button>
                <div className="w-px h-5 bg-slate-300 mx-0.5" />
                <Button type="button" variant="ghost" size="sm" className="h-8 px-2" onClick={() => insertLinePrefix('## ')} title="Heading" data-testid="format-heading">
                  <Heading2 className="w-4 h-4" />
                </Button>
                <Button type="button" variant="ghost" size="sm" className="h-8 px-2" onClick={() => insertLinePrefix('- ')} title="Bullet List" data-testid="format-list">
                  <List className="w-4 h-4" />
                </Button>
                <Button type="button" variant="ghost" size="sm" className="h-8 px-2" onClick={() => insertLinePrefix('1. ')} title="Numbered List" data-testid="format-ordered-list">
                  <ListOrdered className="w-4 h-4" />
                </Button>
                <Button type="button" variant="ghost" size="sm" className="h-8 px-2" onClick={() => insertLinePrefix('---\n')} title="Horizontal Rule" data-testid="format-hr">
                  <Minus className="w-4 h-4" />
                </Button>
                <div className="w-px h-5 bg-slate-300 mx-0.5" />
                <Button type="button" variant="outline" size="sm" className="h-8" onClick={insertImage}>
                  <Image className="w-4 h-4 mr-1" />
                  Image
                </Button>
                <Button type="button" variant="outline" size="sm" className="h-8" onClick={insertYouTube}>
                  <Youtube className="w-4 h-4 mr-1" />
                  YouTube
                </Button>
              </div>
              <Textarea
                ref={contentRef}
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder={"Write your post here...\n\nLeave a blank line between paragraphs for spacing.\n\nUse **bold text** for emphasis.\nUse ## for headings.\nEmojis work too! Just type them directly."}
                rows={14}
                className="text-sm leading-relaxed"
                data-testid="blog-content"
              />
              <p className="text-xs text-slate-400">
                Tip: Press Enter twice for a new paragraph. Use **text** for <strong>bold</strong>, *text* for <em>italic</em>, ## for headings.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Tags (comma-separated)</Label>
              <Input
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="tutorial, tips, beginner"
                data-testid="blog-tags"
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <Label>Publish</Label>
                <p className="text-xs text-slate-500">Make this post visible to everyone</p>
              </div>
              <Switch
                checked={formData.is_published}
                onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setEditorOpen(false)}>Cancel</Button>
              <Button onClick={savePost} disabled={saving} data-testid="save-blog-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {editingPost ? 'Update Post' : 'Create Post'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlogManager;
